import { memo, useCallback, useMemo } from 'react';

import { useTrackOpenIntercomChat } from '@air/analytics';
import { Badge } from '@air/primitive-badge';
import { useAirModal } from '@air/provider-modal';
import { cn } from '@air/utils-tailwind';

import { PlansModal } from '~/components/Modals/PlansModal/PlansModal';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { useStorage } from '~/swr-hooks/subscriptions/useStorage';
import { canManageSubscription } from '~/utils/permissions/workspacePermissions';

export type StorageUpgradeCTAProps = {
  onUpgradeClick?: () => void;
};

const getTitleText = ({ isFreePlan, isOutOfStorage }: { isFreePlan: boolean; isOutOfStorage: boolean }) => {
  if (isFreePlan) {
    return "You're on the free plan";
  }
  if (!isOutOfStorage) {
    return "You're almost out of space!";
  }

  return 'Your workspace is out of storage.';
};

const getDescriptionText = ({
  isFreePlan,
  isOnEnterprisePlan,
}: {
  isFreePlan: boolean;
  isOnEnterprisePlan: boolean;
}) => {
  if (isFreePlan) {
    return 'Upgrade for more storage, more users, and advanced features.';
  }
  if (isOnEnterprisePlan) {
    return 'Talk to Support about upgrading your storage.';
  }

  return 'Upgrade to Enterprise for more space, custom roles, and more.';
};

export const StorageUpgradeCTA = memo(({ onUpgradeClick }: StorageUpgradeCTAProps) => {
  const { data: subscription } = useSubscriptionContext();
  const { data: storage } = useStorage();
  const isFreePlan = !!subscription?.isInFreePlan;
  const isOutOfStorage = !!storage?.isOutOfStorage;
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const canEditPlan = canManageSubscription(permissions);
  const [showPlansModal] = useAirModal(PlansModal);
  const { trackOpenIntercomChat } = useTrackOpenIntercomChat();
  const isFull = !isFreePlan && isOutOfStorage;
  const isAlmostFull = !isFreePlan && !isOutOfStorage;

  const handleClick = useCallback(() => {
    if (subscription?.isOnEnterprisePlan) {
      trackOpenIntercomChat();
    } else {
      showPlansModal();
    }
    onUpgradeClick?.();
  }, [subscription?.isOnEnterprisePlan, trackOpenIntercomChat, showPlansModal, onUpgradeClick]);

  const content = useMemo(
    () => (
      <>
        <div className="flex w-full">
          <p data-testid="STORAGE_UPGRADE_CTA_TITLE" className="flex-1 font-semibold text-12">
            {getTitleText({ isFreePlan, isOutOfStorage })}
          </p>
          {(isFreePlan || !isOutOfStorage) && (
            <Badge appearance="pill" color={isAlmostFull ? 'yellow' : 'blue'}>
              Upgrade
            </Badge>
          )}
        </div>
        <p data-testid="STORAGE_UPGRADE_CTA_DESCRIPTION" className="mt-1 whitespace-pre-wrap text-12">
          {getDescriptionText({ isFreePlan, isOnEnterprisePlan: !!subscription?.isOnEnterprisePlan })}
          {!canEditPlan ? ' Contact your workspace admin to upgrade.' : ''}
        </p>
      </>
    ),
    [canEditPlan, isAlmostFull, isFreePlan, isOutOfStorage, subscription?.isOnEnterprisePlan],
  );

  const parentClassNames = cn(
    'flex flex-col rounded-4 border ads:border-default border-gray-4 ads:bg-raised bg-gray-2 p-2 text-left ads:text-secondary text-gray-11 ads:hover:bg-hover-subtle hover:bg-gray-3',
    isFull && 'ads:bg-error-subtle bg-red-3 ads:text-error text-red-11 ads:hover:bg-ads-flash-4 hover:bg-red-4',
    isAlmostFull &&
      'group ads:bg-warning bg-yellow-3 ads:text-warning text-yellow-11 ads:hover:bg-ads-amber-4 hover:bg-yellow-4',
  );

  if (!canEditPlan) {
    return <div className={parentClassNames}>{content}</div>;
  }

  return (
    <button data-testid="STORAGE_UPGRADE_CTA_BUTTON" className={parentClassNames} onClick={handleClick} type="button">
      {content}
    </button>
  );
});

StorageUpgradeCTA.displayName = 'StorageUpgradeCTA';
